import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import PropTypes from "prop-types";

const ApplicationDialogContext = createContext({
  alert: () => Promise.resolve(true),
  confirm: () => Promise.resolve(false),
});

// Designer modals use z-index values up to 1600. Application-level confirmations
// must stay above them so actions such as deleting a component remain clickable.
const APPLICATION_DIALOG_Z_INDEX = 2000;

const normalizeOptions = (options, fallbackTitle) =>
  typeof options === "string"
    ? { title: fallbackTitle, message: options }
    : { title: fallbackTitle, ...(options || {}) };

export function ApplicationDialogProvider({ children }) {
  const sequenceRef = useRef(0);
  const settledIdsRef = useRef(new Set());
  const [queue, setQueue] = useState([]);
  const activeDialog = queue[0] || null;

  const requestDialog = useCallback((type, options) =>
    new Promise((resolve) => {
      sequenceRef.current += 1;
      const fallbackTitle = type === "confirm" ? "Confirm" : "Notification";
      setQueue((current) => [
        ...current,
        {
          id: sequenceRef.current,
          type,
          resolve,
          ...normalizeOptions(options, fallbackTitle),
        },
      ]);
    }), []);

  const confirm = useCallback((options) => requestDialog("confirm", options), [requestDialog]);
  const alert = useCallback((options) => requestDialog("alert", options), [requestDialog]);

  const settleDialog = useCallback((accepted) => {
    if (!activeDialog || settledIdsRef.current.has(activeDialog.id)) return;
    settledIdsRef.current.add(activeDialog.id);
    activeDialog.resolve(activeDialog.type === "confirm" ? accepted === true : true);
    setQueue((current) => current.filter((item) => item.id !== activeDialog.id));
    window.setTimeout(() => settledIdsRef.current.delete(activeDialog.id), 0);
  }, [activeDialog]);

  const value = useMemo(() => ({ alert, confirm }), [alert, confirm]);
  const confirmLabel = activeDialog?.confirmLabel || (activeDialog?.type === "confirm" ? "Confirm" : "OK");

  return (
    <ApplicationDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={Boolean(activeDialog)}
        onClose={() => settleDialog(false)}
        container={() => document.fullscreenElement || document.body}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: APPLICATION_DIALOG_Z_INDEX }}
        aria-labelledby="application-dialog-title"
      >
        <DialogTitle id="application-dialog-title">{activeDialog?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>{activeDialog?.message}</DialogContentText>
          {activeDialog?.detail && (
            <DialogContentText sx={{ mt: 1.5, color: "text.primary", fontWeight: 600 }}>
              {activeDialog.detail}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {activeDialog?.type === "confirm" && (
            <Button
              color="inherit"
              variant="contained"
              sx={{
                bgcolor: "grey.200",
                boxShadow: "none",
                color: "text.primary",
                fontWeight: 700,
                "&:hover": {
                  bgcolor: "grey.300",
                  boxShadow: "none",
                },
              }}
              onClick={() => settleDialog(false)}
            >
              {activeDialog.cancelLabel || "Cancel"}
            </Button>
          )}
          <Button
            color={activeDialog?.confirmColor || "info"}
            variant="contained"
            autoFocus
            onClick={() => settleDialog(true)}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ApplicationDialogContext.Provider>
  );
}

ApplicationDialogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useApplicationDialog = () => {
  return useContext(ApplicationDialogContext);
};
